import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import OnboardingGate from "@/components/OnboardingGate";
import { InstallBanner, InstallButton } from "@/components/PwaInstall";
import { ThemeToggle } from "@/components/ThemeToggle";
import { isValidPhone } from "@/lib/constants";
import {
  PackageSearch, Truck, LayoutList, MessageSquare,
  CreditCard, ShieldCheck, LogOut, Menu, X, User, AlertTriangle, Bell, LifeBuoy, Phone, Wrench,
} from "lucide-react";

function TrialBanner() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  if (!auth || auth.is_owner) return null;
  if (auth.sub_active) return null;
  const daysLeft = auth.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(auth.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  const expired = !auth.trial_active;
  return (
    <div data-testid="trial-banner" className={`flex items-center justify-between gap-3 px-6 py-2.5 text-sm border-b ${expired ? "bg-destructive/15 border-destructive/30" : "bg-amber-500/10 border-amber-500/25"}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-4 h-4 ${expired ? "text-destructive" : "text-amber-400"}`} />
        {expired
          ? <span>Your free trial has ended. Subscribe to keep posting and matching freight.</span>
          : <span>Free trial active — <b className="font-mono-num">{daysLeft}</b> day{daysLeft !== 1 ? "s" : ""} left.</span>}
      </div>
      <Button data-testid="banner-subscribe-btn" size="sm" onClick={() => navigate("/app/subscription")} className="rounded-full h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90">
        {expired ? "Subscribe now" : "Upgrade"}
      </Button>
    </div>
  );
}

function ContactBanner() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  if (!auth || auth.is_owner) return null;
  const phone = auth.user?.phone;
  // Only nudge members who HAVE a number saved that is not a valid reachable number.
  if (!phone || isValidPhone(phone)) return null;
  return (
    <div data-testid="contact-banner" className="flex items-center justify-between gap-3 px-6 py-2.5 text-sm border-b bg-primary/10 border-primary/25">
      <div className="flex items-center gap-2">
        <Phone className="w-4 h-4 text-primary shrink-0" />
        <span>Your saved contact number looks incorrect — update it so transporters can reach you on WhatsApp.</span>
      </div>
      <Button data-testid="contact-banner-fix-btn" size="sm" onClick={() => navigate("/app/profile")} className="rounded-full h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
        Fix number
      </Button>
    </div>
  );
}

export default function AppShell() {
  const { auth, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const user = auth?.user;

  useEffect(() => {
    const fetchUnread = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    fetchUnread();
    const i = setInterval(fetchUnread, 15000);
    return () => clearInterval(i);
  }, []);

  if (auth && !auth.is_owner && !auth.user?.phone) {
    return <OnboardingGate />;
  }

  const nav = [
    { to: "/app", label: "Load Board", icon: PackageSearch, end: true },
    { to: "/app/trucks", label: "Truck Board", icon: Truck },
    { to: "/app/repair", label: "Repair Centres", icon: Wrench },
    { to: "/app/postings", label: "My Postings", icon: LayoutList },
    { to: "/app/messages", label: "Messages", icon: MessageSquare },
    { to: "/app/notifications", label: "Alerts", icon: Bell },
    { to: "/app/subscription", label: "Subscription", icon: CreditCard },
    { to: "/app/profile", label: "Profile", icon: User },
  ];
  if (auth?.is_owner) nav.push({ to: "/app/admin", label: "Admin", icon: ShieldCheck });

  const NavItems = () => (
    <>
      {nav.map((n) => (
        <NavLink
          key={n.to} to={n.to} end={n.end}
          data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              isActive ? "bg-primary/15 text-primary border border-primary/30 shadow-[0_0_16px_hsl(var(--primary)/0.18)]" : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
            }`
          }
        >
          <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
          {n.label}
          {n.label === "Alerts" && unread > 0 && (
            <span data-testid="alerts-unread-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-mono-num">{unread}</span>
          )}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border glass p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-2 py-3 mb-4">
          <img src="/logo-icon.png" alt="Reefer Sathi logo" data-testid="sidebar-logo" className="w-8 h-8 object-contain drop-shadow-[0_0_14px_hsl(var(--primary)/0.35)]" />
          <span className="font-heading font-extrabold text-lg tracking-tight text-frost">Reefer Sathi</span>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          <NavItems />
        </nav>
        <div className="border-t border-border pt-3 mt-3">
          <div className="px-1 pb-2"><ThemeToggle /></div>
          <InstallButton />
          <a href="mailto:info@reefersathi.com" data-testid="sidebar-contact-link" className="flex items-center gap-2 px-2 py-2 mb-1 text-xs text-muted-foreground hover:text-primary transition-colors">
            <LifeBuoy className="w-4 h-4" /> Need help? Contact us
          </a>
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9 border border-border">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-secondary text-xs">{user?.name?.[0] || "U"}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{auth?.is_owner ? "Owner / Admin" : "Transporter"}</p>
            </div>
            <button data-testid="logout-btn" onClick={() => setConfirmLogout(true)} className="text-muted-foreground hover:text-destructive transition-colors p-1.5">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="app-mobile-header lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-3 border-b border-border glass">
        <div className="flex items-center gap-2">
          <img src="/logo-icon.png" alt="Reefer Sathi logo" data-testid="mobile-logo" className="w-6 h-6 object-contain" />
          <span className="font-heading font-extrabold text-frost">Reefer Sathi</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="app-mobile-menu lg:hidden fixed inset-0 z-20 bg-background/95 backdrop-blur pt-16 px-4">
          <nav className="flex flex-col gap-1">
            <NavItems />
            <div className="px-2.5 mt-3 mb-1"><ThemeToggle /></div>
            <div className="px-2.5"><InstallButton onDone={() => setOpen(false)} /></div>
            <button data-testid="mobile-logout-btn" onClick={() => { setOpen(false); setConfirmLogout(true); }} className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-destructive mt-1">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="app-main-mobile flex-1 min-w-0 lg:pt-0">
        <TrialBanner />
        <ContactBanner />
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      <AlertDialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Sign out of Reefer Sathi?</AlertDialogTitle>
            <AlertDialogDescription>You'll need to sign in with Google again to access your boards and messages.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-logout-btn" className="rounded-full">Stay signed in</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-logout-btn" onClick={logout} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">Sign out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InstallBanner />
    </div>
  );
}
